import useStores from '@hooks/useStore';
import type { Workspace } from '@stratumn/studio-shared';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import type { WorkspaceFormValues } from '../form/form.workspace';

export const useWorkspaceActions = (processId: string) => {
  const { workspaceStore } = useStores();

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingWorkspace, setDeletingWorkspace] = useState<Workspace | null>(
    null
  );

  const handleCreate = useCallback(
    async (values: WorkspaceFormValues) => {
      try {
        await workspaceStore.createWorkspace(processId, {
          name: values.name.trim(),
          color: values.color,
          version: values.version,
          tags: {}
        });
        toast.success(`Workspace "${values.name}" created`);
        setShowCreateForm(false);
      } catch {
        toast.error('Failed to create workspace');
      }
    },
    [processId, workspaceStore]
  );

  const handleUpdate = useCallback(
    async (values: WorkspaceFormValues) => {
      if (!editingId) {
        return;
      }
      try {
        await workspaceStore.updateWorkspace(processId, editingId, {
          name: values.name.trim(),
          color: values.color,
          version: values.version
        });
        toast.success('Workspace updated');
        setEditingId(null);
      } catch {
        toast.error('Failed to update workspace');
      }
    },
    [processId, editingId, workspaceStore]
  );

  const handleDelete = useCallback(async () => {
    if (!deletingWorkspace) {
      return;
    }

    try {
      await workspaceStore.deleteWorkspace(processId, deletingWorkspace.id);
      toast.success(`Workspace "${deletingWorkspace.name}" deleted`);
      if (editingId === deletingWorkspace.id) {
        setEditingId(null);
      }
    } catch {
      toast.error('Failed to delete workspace');
    } finally {
      setDeletingWorkspace(null);
    }
  }, [processId, deletingWorkspace, editingId, workspaceStore]);

  return {
    showCreateForm,
    setShowCreateForm,
    editingId,
    setEditingId,
    deletingWorkspace,
    setDeletingWorkspace,
    handleCreate,
    handleUpdate,
    handleDelete
  };
};
